import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Talent, UserProfile } from "@shared/schema";

/**
 * Shared talent-scoping hook for the Talent module pages (Callsheets,
 * Contacts, Calendar). Admins see every talent in the workspace; external
 * talent users are scoped to the talent profile(s) linked to their account.
 */
export function useOwnedTalents() {
  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });
  const { data: talent = [], isLoading } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  const isAdmin = profile?.role === "admin" || profile?.role === "super_admin";

  const ownedTalents = useMemo(
    () => (talent || []).filter((t) => isAdmin || t.userId === profile?.userId),
    [talent, profile, isAdmin],
  );

  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedTalentId && ownedTalents.length > 0) {
      setSelectedTalentId(ownedTalents[0].id);
    }
  }, [ownedTalents, selectedTalentId]);

  const selectedTalent =
    ownedTalents.find((t) => t.id === selectedTalentId) || null;

  return {
    profile,
    isAdmin,
    ownedTalents,
    selectedTalentId,
    setSelectedTalentId,
    selectedTalent,
    isLoading,
  };
}
